import type { FormikProps } from '@axiom/ui';
import {
  Button,
  CondensedHeader,
  CondensedMedium,
  Form,
  Grid,
  GridColumn,
  Gutter,
  Layout,
  BlockerUtil,
  TaxonomyUtil,
  useApi,
  Well,
  useBreakpoint,
  LargeHeader,
  ToastUtil,
  FormSchemaUtil,
  FluidButtonLayout,
  MethodNotAllowedError,
} from '@axiom/ui';
import {
  PositionsConst,
  ProjectNeedsDefaultRemoteLocation,
} from '@axiom/const';
import type {
  DSSuggestedIntake,
  ProjectNeedsWithLegalSkills,
  Account,
} from '@axiom/validation';
import { ProjectNeedsWithLegalSkillsSchema } from '@axiom/validation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from 'react-router';
import type { z } from 'zod';

import { InstantMatchesApi } from '../../api/instant-matches-api';
import { IntakeSuggestApi } from '../../api/intake-suggest-api';
import { LanguagesApi } from '../../api/languages-api';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { OpportunityIntakeApi } from '../../api/opportunity-intake-api';
import { AccountIntakeApi } from '../../api/account-intake-api';
import { AccountSubmissionsApi } from '../../api/account-submissions-api';
import { SubmissionApi } from '../../api/submission-api';
import type { useUserAccountData } from '../../hooks/useUserAccountData';
import { EngagementUtil } from '../../utils/engagement-util';

import { DirectEngagementsFormAdditionalInfo } from './DirectEngagementsFormAdditionalInfo';
import { DirectEngagementsFormExpertiseCard } from './DirectEngagementsFormExpertiseCard';
import { DirectEngagementsFormIntakeSuggestionModal } from './DirectEngagementsFormIntakeSuggestionModal';
import { DirectEngagementsFormScheduleCard } from './DirectEngagementsFormScheduleCard';
import { DirectEngagementsFormWorksiteModal } from './DirectEngagementsFormWorksiteModal';
import type { PositionDescriptionSection } from './direct-engagements.types';
import { DirectEngagementsFormIntakeUtil } from './direct-engagements-form-intake-util';
import { DirectEngagementsFormUtil } from './direct-engagements-form-util';
import { DirectEngagementsFormSummary } from './DirectEngagementsFormSummary';

const intakeLoaderHeaders = [
  'Reading between the lines...',
  'Identifying what matters most...',
  'Building a picture of your requirements...',
];
const intakeLoaderDescription =
  'Analyzing your job description and detecting your legal needs';

export type DirectEngagementsFormProps = {
  account: Account;
  user: ReturnType<typeof useUserAccountData>['user'];
};

export const DirectEngagementsForm = ({
  account,
  user,
}: DirectEngagementsFormProps) => {
  const { accountId, submissionId } = useParams();
  const location = useLocation();
  if (!accountId) {
    throw new Error('No accountId provided');
  }
  const [searchParams] = useSearchParams();
  const paramContactId = searchParams.get('contactId');
  const isEditForm = !!submissionId;
  // Start date handed over from the intake intro (ClientIntent) via router state.
  // Create flow only, so an edit never has its persisted date overridden.
  const routerState = location.state as { startDate?: string } | null;
  const introStartDate = isEditForm ? undefined : routerState?.startDate;

  const navigate = useNavigate();
  const [companyLocationInputValue, setCompanyLocationInputValue] =
    useState<ProjectNeedsWithLegalSkills['companyLocation']>();
  const [showWorksiteModal, setShowWorksiteModal] = useState(false);
  const [showIntakeSuggestModal, setShowIntakeSuggestModal] =
    useState(!isEditForm);
  const [forceExpandAccordion, setForceExpandAccordion] = useState(false);
  const [positionDescription, setPositionDescription] = useState<
    PositionDescriptionSection[]
  >([]);

  const [{ data: taxonomy }, { data: languages }, { data: intakeData }] =
    useApi(
      TaxonomyApi.readTaxonomy(),
      LanguagesApi.readLanguages(),
      AccountIntakeApi.readAccountIntake(
        accountId,
        submissionId ? { submissionId } : undefined
      )
    );

  const contactId = user.contactId || paramContactId || undefined;

  const isOptionalDataPrefilled = !!(
    intakeData?.legalTechSkills?.length ||
    intakeData?.generalSkills?.length ||
    intakeData?.languageId ||
    intakeData?.levelOfExperience ||
    intakeData?.industry ||
    intakeData?.additionalDetailsForTalent
  );

  const { isLargeScreen } = useBreakpoint();

  const { legalSkills, skillGroups } = taxonomy;

  const [filteredSkillGroups, setFilteredSkillGroups] = useState(
    TaxonomyUtil.getFocusAreaSkillGroups(
      intakeData?.primaryFocusArea ?? '',
      skillGroups
    )
  );

  const initialValues: Partial<ProjectNeedsWithLegalSkills> = useMemo(() => {
    const otherFocusAreas = TaxonomyUtil.filterSelectableFocusAreas(
      taxonomy,
      intakeData?.otherFocusAreas ?? []
    );

    const primaryFocusArea = TaxonomyUtil.isFocusAreaSelectable(
      taxonomy,
      intakeData?.primaryFocusArea
    )
      ? intakeData?.primaryFocusArea
      : undefined;

    const countryCodeDefault =
      intakeData?.addressCountryCode ??
      DirectEngagementsFormIntakeUtil.defaultCountryCode({ account });

    return intakeData
      ? {
          ...intakeData,
          addressCountryCode: countryCodeDefault,
          companyLocation:
            intakeData.companyLocation ?? ProjectNeedsDefaultRemoteLocation,
          levelOfExperience: intakeData.levelOfExperience,
          legalSkillsAnswers: primaryFocusArea
            ? DirectEngagementsFormUtil.addPrefilledLegalSkills({
                filteredSkillGroups,
                intakeLegalSkills: intakeData.legalSkills,
                legalSkills,
              })
            : [],
          primaryFocusArea,
          otherFocusAreas,
          startDate: introStartDate ?? intakeData.startDate,
        }
      : {
          addressCountryCode: countryCodeDefault,
          startDate: introStartDate,
        };
  }, [intakeData, introStartDate]);

  const formikRef =
    useRef<FormikProps<z.infer<typeof ProjectNeedsWithLegalSkillsSchema>>>(
      null
    );

  const updateFormValuesAndDescription = (
    updatedValues: Partial<ProjectNeedsWithLegalSkills>
  ) => {
    setPositionDescription(() =>
      EngagementUtil.buildPositionDescription({
        values: updatedValues,
        accountName: account.calculatedName,
        taxonomy,
        languages,
      })
    );
  };

  useEffect(() => {
    updateFormValuesAndDescription(initialValues);
  }, [initialValues]);

  const handleIntakeSuggestion = (response: DSSuggestedIntake) => {
    if (!formikRef.current) return;

    const { mergedValues, updatedSkillGroups, shouldExpandAccordion } =
      DirectEngagementsFormIntakeUtil.applyIntakeSuggestion({
        response,
        currentValues: formikRef.current.values,
        skillGroups,
        legalSkills,
      });

    if (updatedSkillGroups) {
      setFilteredSkillGroups(updatedSkillGroups);
    }

    if (shouldExpandAccordion) {
      setForceExpandAccordion(true);
    }

    void formikRef.current.setValues(mergedValues);
    updateFormValuesAndDescription(mergedValues);
  };

  const handleIntakeSuggestContinue = async (intakeText: string) => {
    setShowIntakeSuggestModal(false);
    const blockerEvent = BlockerUtil.block({
      headers: intakeLoaderHeaders,
      description: intakeLoaderDescription,
    });
    try {
      const { data: intakeSuggestion } =
        await IntakeSuggestApi.createIntakeSuggestion(intakeText);
      handleIntakeSuggestion(intakeSuggestion);
    } catch {
      ToastUtil.add({
        name: 'intake-suggest-error-toast',
        type: 'error',
        dismissible: true,
        children: (
          <>
            <CondensedHeader>Something went wrong</CondensedHeader>
            <CondensedMedium>
              Please continue to describe your legal needs.
            </CondensedMedium>
          </>
        ),
      });
    } finally {
      BlockerUtil.unblock(blockerEvent);
    }
  };

  const handleSubmit = async (
    formData: Partial<ProjectNeedsWithLegalSkills>
  ) => {
    if (
      formData.companyLocation?.locationName ===
      ProjectNeedsDefaultRemoteLocation.locationName
    ) {
      formData.companyLocation = undefined;
    }

    const { addressCity, addressState, addressStreet, addressZip } =
      EngagementUtil.extractAddressFields(formData.companyLocation);

    const selectedLegalSkills = (formData.legalSkillsAnswers ?? [])
      .flat()
      .filter(Boolean)
      .map(v => v as string);

    const body = {
      ...DirectEngagementsFormUtil.removeDynamicFields({ data: formData }),
      accountId,
      addressCity,
      addressCountryCode:
        formData.worksite === PositionsConst.Worksites.Remote
          ? formData.addressCountryCode
          : null,
      addressState,
      addressStreet,
      addressZip,
      companyLocation:
        formData.worksite === PositionsConst.Worksites.Remote
          ? null
          : formData.companyLocation,
      legalSkills:
        selectedLegalSkills.length > 0 ? selectedLegalSkills : undefined,
      contactId,
    };

    let targetSubmissionId = submissionId;

    if (isEditForm) {
      try {
        await OpportunityIntakeApi.updateOpportunityIntake({
          ...body,
          opportunityId: intakeData?.opportunityId,
        });
        await AccountIntakeApi.updateAccountIntake(
          accountId,
          {
            ...body,
            opportunityId: intakeData?.opportunityId,
          },
          submissionId ? { submissionId } : undefined
        );
      } catch (error) {
        if (error instanceof MethodNotAllowedError) {
          ToastUtil.add({
            name: 'update-not-allowed-toast',
            type: 'critical',
            dismissible: true,
            children: (
              <CondensedMedium>
                You cannot edit requirements at this time. Please use 'Need
                Help' to contact Axiom if you need to make changes to your
                engagement.
              </CondensedMedium>
            ),
          });
          void navigate(
            `/account/${accountId}/submission/${submissionId}/matches`
          );
        }
      }
    } else {
      const { data } = await OpportunityIntakeApi.createOpportunityIntake(body);

      targetSubmissionId = data.submissionId;
    }

    if (!targetSubmissionId) {
      throw new Error('SubmissionId is missing');
    }

    const [
      _,
      {
        data: { numMatchingCandidates },
      },
    ] = await Promise.all([
      AccountIntakeApi.refreshAccountIntake(accountId),
      InstantMatchesApi.createInstantMatches(targetSubmissionId),
    ]);

    // Only show this toast for US based opportunities that do not have matches
    if (
      numMatchingCandidates === 0 &&
      DirectEngagementsFormUtil.isInsideUnitedStates({
        addressCountryCode: body.addressCountryCode,
        companyLocation: body.companyLocation,
      })
    ) {
      const editLink = location.pathname.endsWith('intake')
        ? `${location.pathname}/${targetSubmissionId}${location.search}`
        : location;

      ToastUtil.add({
        name: 'no-matches-found-toast',
        type: 'critical',
        dismissible: true,
        children: (
          <div>
            <CondensedHeader>No matches found</CondensedHeader>
            <CondensedMedium>
              Try editing your{' '}
              <Link data-test="no-matches-edit-link" to={editLink}>
                legal needs
              </Link>
            </CondensedMedium>
          </div>
        ),
      });
    }

    await InstantMatchesApi.refreshInstantMatches(targetSubmissionId);
    await AccountSubmissionsApi.refreshAccountSubmissions(accountId);
    await SubmissionApi.refreshSubmissionDetails(targetSubmissionId);

    void navigate(
      DirectEngagementsFormUtil.getMatchesUrl({
        accountId,
        submissionId: targetSubmissionId,
      })
    );
  };

  const renderFormHeader = () => {
    if (isEditForm) {
      return (
        <LargeHeader name="DIRECT_ENGAGEMENTS_FORM_EDIT_HEADER">
          Edit your legal needs to update your matches
        </LargeHeader>
      );
    }

    return (
      <Well
        background="high attention"
        name="DIRECT_ENGAGEMENTS_FORM_WELL_HEADER"
      >
        <LargeHeader>
          Share what you know about your legal needs to see instant Axiom talent
          matches.
        </LargeHeader>
      </Well>
    );
  };

  const renderMatchesButton = () => {
    if (!submissionId) return null;

    return (
      <Gutter bottom="16px">
        <Button
          to={DirectEngagementsFormUtil.getMatchesUrl({
            accountId,
            submissionId,
          })}
          name="BACK_TO_MATCHES"
          pattern="primary"
          variation="minimal"
          icon="arrow-left"
          iconPosition="left"
        >
          MATCHES
        </Button>
      </Gutter>
    );
  };

  const renderCancelButton = () => {
    if (!submissionId) return null;

    const url = DirectEngagementsFormUtil.getMatchesUrl({
      accountId,
      submissionId,
    });

    return (
      <Button
        onClick={() => {
          void navigate(url);
        }}
        name="CANCEL"
        variation="outline"
      >
        CANCEL
      </Button>
    );
  };

  useEffect(() => {
    if (formikRef.current && positionDescription.length > 0) {
      void formikRef.current.setValues({
        ...formikRef.current.values,
        positionDescription:
          EngagementUtil.serializePositionDescription(positionDescription),
      });
    }
  }, [positionDescription]);

  return (
    <>
      {showIntakeSuggestModal ? (
        <DirectEngagementsFormIntakeSuggestionModal
          onClose={() => {
            setShowIntakeSuggestModal(false);
          }}
          onContinue={(intakeText: string) => {
            void handleIntakeSuggestContinue(intakeText);
          }}
        />
      ) : null}
      {renderMatchesButton()}
      <Form
        name="direct-engagements-form"
        schema={ProjectNeedsWithLegalSkillsSchema}
        initialValues={initialValues}
        onSubmit={async formData => {
          await handleSubmit({ ...initialValues, ...formData });
        }}
        formRef={formikRef}
        onChange={() => {
          const values = formikRef.current?.values ?? {};
          updateFormValuesAndDescription(values);
        }}
      >
        {({ values, setValues, isValid, submitCount, fireSubmit }) => {
          return (
            <Grid centered>
              <GridColumn largeScreenWidth={8} tabletWidth={12}>
                {renderFormHeader()}
                <Gutter bottom="24px" />
                <DirectEngagementsFormExpertiseCard
                  filteredSkillGroups={filteredSkillGroups}
                  setFilteredSkillGroups={setFilteredSkillGroups}
                  setValues={setValues}
                  taxonomy={taxonomy}
                  values={values}
                />
                <Gutter top="24px" />
                <DirectEngagementsFormAdditionalInfo
                  expanded={isOptionalDataPrefilled || forceExpandAccordion}
                  isValid={isValid}
                  languages={languages}
                  submitCount={submitCount}
                  taxonomy={taxonomy}
                  values={values}
                />
                <Gutter top="24px" />
                <DirectEngagementsFormScheduleCard
                  account={account}
                  companyLocationInputValue={companyLocationInputValue}
                  setCompanyLocationInputValue={setCompanyLocationInputValue}
                  setShowWorksiteModal={setShowWorksiteModal}
                  setValues={setValues}
                  values={values}
                />
                <Gutter top="24px" />
                <Layout
                  position="center"
                  reversibleOn="mobile"
                  stackableOn="mobile"
                >
                  <FluidButtonLayout stackableOn="mobile">
                    {renderCancelButton()}
                    <Button
                      name="SUBMIT"
                      onClick={async () => {
                        const { errors } = FormSchemaUtil.validate(
                          ProjectNeedsWithLegalSkillsSchema,
                          values
                        );
                        if (errors) {
                          ToastUtil.add({
                            name: 'invalid-engagement-form-toast',
                            type: 'error',
                            dismissible: 3,
                            children: (
                              <CondensedHeader>
                                Please provide all details and resolve any
                                errors before proceeding
                              </CondensedHeader>
                            ),
                          });
                        }
                        await fireSubmit();
                      }}
                    >
                      {isEditForm ? 'SHOW NEW MATCHES' : 'SHOW MY MATCHES'}
                    </Button>
                  </FluidButtonLayout>
                </Layout>
                {showWorksiteModal ? (
                  <DirectEngagementsFormWorksiteModal
                    onClose={() => {
                      setShowWorksiteModal(false);
                    }}
                    onChangeToRemote={async () => {
                      const updatedValues = {
                        ...values,
                        companyLocation: ProjectNeedsDefaultRemoteLocation,
                        worksite: 'Remote' as const,
                      };
                      await setValues(updatedValues);
                      updateFormValuesAndDescription(updatedValues);
                    }}
                  />
                ) : null}
              </GridColumn>
              {isLargeScreen ? (
                <GridColumn largeScreenWidth={4}>
                  <DirectEngagementsFormSummary
                    positionDescription={positionDescription}
                  />
                </GridColumn>
              ) : null}
            </Grid>
          );
        }}
      </Form>
    </>
  );
};
